// Real-Time Data Sync & Conflict Resolution Service
// Netflix/Duolingo-style multi-device sync with conflict resolution
// Enhanced with request tracing and resilient calls

import { createClient, RealtimeChannel } from '@supabase/supabase-js';
import { userAuth } from './userAuth';
import { logger } from '../utils/logger';
import { optimizedQueries } from './optimizedQueries';
import { requestTracing } from './requestTracing';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

export interface UserProgress {
  userId: string;
  testHistory: any[];
  studyTime: number;
  averageScore: number;
  lastSync: string;
  version: number; // For conflict resolution
  deviceId: string; // Track which device made the change
}

export interface SyncConflict {
  local: UserProgress;
  remote: UserProgress;
  resolution: 'local' | 'remote' | 'merged';
}

class RealTimeSyncService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private syncQueue: Array<{ userId: string; progress: UserProgress }> = [];
  private isOnline: boolean = navigator.onLine;
  private deviceId: string;
  private syncInProgress: boolean = false;

  constructor() {
    // Generate unique device ID
    this.deviceId = this.getOrCreateDeviceId();
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Initialize service worker for background sync
    this.initializeBackgroundSync();
  }

  // Get or create device ID
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  // Subscribe to real-time updates for a user
  subscribeToUserProgress(userId: string, callback: (progress: UserProgress) => void): () => void {
    // Unsubscribe from existing channel if any
    this.unsubscribeFromUserProgress(userId);

    const channelName = `user-progress-${userId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_progress',
          filter: `user_id=eq.${userId}`
        },
        async (payload: any) => {
          logger.debug('Real-time update received:', payload);
          
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const remoteProgress = await this.parseRemoteProgress(payload.new);
            
            // Check if this update came from this device (avoid circular updates)
            if (remoteProgress.deviceId === this.deviceId) {
              logger.debug('Update from same device, skipping callback');
              return;
            }

            // Resolve conflicts if local data exists
            const localProgress = await this.loadLocalProgress(userId);
            if (localProgress) {
              const resolved = await this.resolveConflict(localProgress, remoteProgress);
              callback(resolved);
            } else {
              callback(remoteProgress);
            }
          }
        }
      )
      .subscribe((status: any) => {
        logger.debug(`Real-time subscription status for ${userId}:`, status);
      });

    this.channels.set(userId, channel);
    
    // Return unsubscribe function
    return () => this.unsubscribeFromUserProgress(userId);
  }

  // Unsubscribe from real-time updates
  unsubscribeFromUserProgress(userId: string): void {
    const channel = this.channels.get(userId);
    if (channel) {
      supabase.removeChannel(channel);
      this.channels.delete(userId);
    }
  }

  // Save progress with real-time sync
  // Enhanced with request tracing (Netflix/Duolingo style)
  async saveProgress(userId: string, progressData: any): Promise<boolean> {
    return await requestTracing.trace('saveProgress', async () => {
      try {
        // Load existing local progress to get version
        const existing = await this.loadLocalProgress(userId);
        const version = existing ? existing.version + 1 : 1;

        // Create progress object with version and device ID
        const progress: UserProgress = {
          userId,
          testHistory: progressData.testHistory || [],
          studyTime: progressData.studyTime || 0,
          averageScore: progressData.averageScore || 0,
          lastSync: new Date().toISOString(),
          version,
          deviceId: this.deviceId
        };

        // Always save locally first (offline-first)
        await this.saveLocalProgress(userId, progress);

        // Queue for cloud sync (handles offline gracefully)
        if (this.isOnline) {
          await this.syncToCloud(userId, progress);
        } else {
          // Queue for later sync
          this.queueForSync(userId, progress);
        }

        logger.debug('Progress saved with real-time sync:', userId);
        return true;
      } catch (error) {
        logger.error('Error saving progress with real-time sync:', error);
        return false;
      }
    }, { userId });
  }

  // Load progress (local-first, then sync from cloud)
  async loadProgress(userId: string): Promise<UserProgress | null> {
    try {
      // Load from local storage first (fast)
      const localProgress = await this.loadLocalProgress(userId);

      // If online, sync from cloud and merge
      if (this.isOnline) {
        const remoteProgress = await this.loadFromCloud(userId);
        
        if (remoteProgress && localProgress) {
          // Resolve conflicts
          return await this.resolveConflict(localProgress, remoteProgress);
        } else if (remoteProgress) {
          // No local data, use remote
          await this.saveLocalProgress(userId, remoteProgress);
          return remoteProgress;
        }
      }

      return localProgress;
    } catch (error) {
      logger.error('Error loading progress:', error);
      return null;
    }
  }

  // Resolve conflicts between local and remote data
  private async resolveConflict(
    local: UserProgress,
    remote: UserProgress
  ): Promise<UserProgress> {
    // If versions are the same, no conflict
    if (local.version === remote.version) {
      return local;
    }

    // If remote is newer, merge intelligently
    if (new Date(remote.lastSync) > new Date(local.lastSync)) {
      logger.debug('Remote is newer, merging with local');
      return this.mergeProgress(remote, local);
    }

    // If local is newer, merge intelligently
    if (new Date(local.lastSync) > new Date(remote.lastSync)) {
      logger.debug('Local is newer, merging with remote');
      return this.mergeProgress(local, remote);
    }

    // Default: use local (last write wins)
    return local;
  }

  // Merge two progress objects intelligently
  private mergeProgress(primary: UserProgress, secondary: UserProgress): UserProgress {
    // Merge test history (combine unique tests, keep latest scores)
    const mergedHistory = this.mergeTestHistory(
      primary.testHistory || [],
      secondary.testHistory || []
    );

    // Use higher study time
    const studyTime = Math.max(primary.studyTime || 0, secondary.studyTime || 0);

    // Recalculate average score from merged history
    const averageScore = this.calculateAverageScore(mergedHistory);

    return {
      ...primary,
      testHistory: mergedHistory,
      studyTime,
      averageScore,
      lastSync: new Date().toISOString(),
      version: Math.max(primary.version, secondary.version) + 1,
      deviceId: this.deviceId
    };
  }

  // Merge test history arrays (keep unique tests, latest scores)
  private mergeTestHistory(history1: any[], history2: any[]): any[] {
    const merged: Map<string, any> = new Map();

    // Add all tests from history1
    history1.forEach(test => {
      const key = `${test.testId}_${test.date}`;
      merged.set(key, test);
    });

    // Add tests from history2, keeping latest if duplicate
    history2.forEach(test => {
      const key = `${test.testId}_${test.date}`;
      const existing = merged.get(key);
      
      if (!existing || new Date(test.date) > new Date(existing.date)) {
        merged.set(key, test);
      }
    });

    return Array.from(merged.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  // Calculate average score from test history
  private calculateAverageScore(history: any[]): number {
    if (history.length === 0) return 0;
    
    const total = history.reduce((sum, test) => sum + (test.percentage || 0), 0);
    return Math.round(total / history.length);
  }

  // Save to local storage
  private async saveLocalProgress(userId: string, progress: UserProgress): Promise<void> {
    localStorage.setItem(`userProgress_${userId}`, JSON.stringify(progress));
  }

  // Load from local storage
  private async loadLocalProgress(userId: string): Promise<UserProgress | null> {
    const saved = localStorage.getItem(`userProgress_${userId}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        logger.error('Error parsing local progress:', error);
        return null;
      }
    }
    return null;
  }

  // Sync to cloud
  private async syncToCloud(userId: string, progress: UserProgress): Promise<void> {
    if (this.syncInProgress) {
      logger.debug('Sync already in progress, queuing...');
      this.queueForSync(userId, progress);
      return;
    }

    this.syncInProgress = true;

    try {
      const currentUser = userAuth.getCurrentUser();
      if (!currentUser || currentUser.isGuest) {
        return;
      }

      const progressData = {
        test_history: JSON.stringify(progress.testHistory || []),
        study_time: progress.studyTime || 0,
        average_score: progress.averageScore || 0,
        total_tests: (progress.testHistory || []).length,
        last_sync: progress.lastSync,
        version: progress.version,
        device_id: progress.deviceId
      };

      // Use optimized query service (handles caching and batching)
      const success = await optimizedQueries.upsertUserProgress(userId, progressData);

      if (!success) {
        logger.error('Optimized sync failed, queuing for retry');
        this.queueForSync(userId, progress);
      } else {
        logger.debug('Progress synced to cloud successfully (optimized)');
      }
    } catch (error) {
      logger.error('Cloud sync error:', error);
      this.queueForSync(userId, progress);
    } finally {
      this.syncInProgress = false;
      // Process queue if any
      this.processSyncQueue();
    }
  }

  // Load from cloud (optimized with selective fetching)
  private async loadFromCloud(userId: string): Promise<UserProgress | null> {
    try {
      // Use optimized query service (cached, selective columns)
      const data = await optimizedQueries.getUserProgress(userId, true);
      if (!data) return null;

      return this.parseRemoteProgress(data);
    } catch (error) {
      logger.error('Error loading from cloud:', error);
      return null;
    }
  }

  // Parse remote progress from Supabase format
  private parseRemoteProgress(data: any): UserProgress {
    return {
      userId: data.user_id,
      testHistory: data.test_history ? JSON.parse(data.test_history) : [],
      studyTime: data.study_time || 0,
      averageScore: data.average_score || 0,
      lastSync: data.last_sync || data.updated_at,
      version: data.version || 1,
      deviceId: data.device_id || 'unknown'
    };
  }

  // Queue for sync (offline support)
  private queueForSync(userId: string, progress: UserProgress): void {
    // Remove existing entry for this user
    this.syncQueue = this.syncQueue.filter(item => item.userId !== userId);
    
    // Add to queue
    this.syncQueue.push({ userId, progress });
    
    // Store queue in IndexedDB for persistence
    this.saveSyncQueue();
  }

  // Process sync queue
  private async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.syncQueue.length === 0) {
      return;
    }

    const queue = [...this.syncQueue];
    this.syncQueue = [];

    for (const item of queue) {
      try {
        await this.syncToCloud(item.userId, item.progress);
      } catch (error) {
        logger.error('Error processing sync queue:', error);
        // Re-queue on error
        this.queueForSync(item.userId, item.progress);
      }
    }

    this.saveSyncQueue();
  }

  // Save sync queue to IndexedDB
  private async saveSyncQueue(): Promise<void> {
    try {
      localStorage.setItem('syncQueue', JSON.stringify(this.syncQueue));
    } catch (error) {
      logger.error('Error saving sync queue:', error);
    }
  }

  // Load sync queue from IndexedDB
  private loadSyncQueue(): void {
    try {
      const saved = localStorage.getItem('syncQueue');
      if (saved) {
        this.syncQueue = JSON.parse(saved);
      }
    } catch (error) {
      logger.error('Error loading sync queue:', error);
    }
  }

  // Initialize background sync (Service Worker)
  private async initializeBackgroundSync(): Promise<void> {
    if ('serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as any)) {
      try {
        const registration = await navigator.serviceWorker.ready;
        
        // Register background sync
        await (registration as any).sync.register('sync-user-progress');
        
        logger.debug('Background sync registered');
      } catch (error) {
        logger.error('Error registering background sync:', error);
      }
    }
  }

  // Check if user has saved progress
  hasSavedProgress(userId: string): boolean {
    return localStorage.getItem(`userProgress_${userId}`) !== null;
  }

  // Get all saved progress for a user
  getAllProgress(userId: string): any {
    const saved = localStorage.getItem(`userProgress_${userId}`);
    return saved ? JSON.parse(saved) : null;
  }

  // Clear all progress (for testing)
  clearProgress(userId: string): void {
    localStorage.removeItem(`userProgress_${userId}`);
    logger.debug('Progress cleared for user:', userId);
  }
}

export const realTimeSync = new RealTimeSyncService();

